import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Menu, X, ChevronDown, User, LogOut } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useAuth } from '../../context/AuthContext';
import AuthModal from '../modals/AuthModal';
import brand from '../../config/brand';

const categories = [
  { slug: 'news', name: 'News', subcategories: [] },
  {
    slug: 'personal-finance',
    name: 'Personal Finance',
    subcategories: [
      { slug: 'banking', name: 'Banking' },
      { slug: 'credit-cards', name: 'Credit Cards' },
      { slug: 'loans', name: 'Loans' },
      { slug: 'mortgage', name: 'Mortgage' },
      { slug: 'retirement', name: 'Retirement' },
      { slug: 'taxes', name: 'Taxes' },
    ],
  },
  { slug: 'debt-management', name: 'Debt Management', subcategories: [] },
  { slug: 'small-business', name: 'Small Business', subcategories: [] },
  { slug: 'investing', name: 'Investing', subcategories: [] },
  { slug: 'markets', name: 'Markets', subcategories: [] },
];

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <>
      <nav className="navbar sticky top-0 z-50 w-full" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 sm:h-[72px]">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo-link">
              <span className="text-xl sm:text-2xl font-black text-black uppercase tracking-tighter" style={{ fontFamily: "'Merriweather', serif" }}>
                <span className="text-red-600">{brand.namePart1}</span>{brand.namePart2}
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {categories.map((cat) => (
                cat.subcategories.length > 0 ? (
                  <DropdownMenu key={cat.slug}>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="flex items-center px-3 py-2 text-xs font-bold text-black uppercase tracking-wider hover:text-red-600 transition-colors"
                        style={{ fontFamily: "'DM Sans', sans-serif" }}
                        data-testid={`nav-${cat.slug}`}
                      >
                        {cat.name}
                        <ChevronDown className="ml-1 h-3 w-3" strokeWidth={2} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_#000]">
                      <DropdownMenuItem asChild>
                        <Link
                          to={`/category/${cat.slug}`}
                          className="text-black hover:text-red-600 font-medium text-sm"
                          data-testid={`nav-${cat.slug}-all`}
                        >
                          All {cat.name}
                        </Link>
                      </DropdownMenuItem>
                      {cat.subcategories.map((sub) => (
                        <DropdownMenuItem key={sub.slug} asChild>
                          <Link
                            to={`/category/${cat.slug}/${sub.slug}`}
                            className="text-black hover:text-red-600 font-medium text-sm"
                            data-testid={`nav-${cat.slug}-${sub.slug}`}
                          >
                            {sub.name}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="px-3 py-2 text-xs font-bold text-black uppercase tracking-wider hover:text-red-600 transition-colors"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                    data-testid={`nav-${cat.slug}`}
                  >
                    {cat.name}
                  </Link>
                )
              ))}
            </div>

            {/* Right Actions */}
            <div className="flex items-center space-x-2">
              {/* Search */}
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="p-2 text-black hover:text-red-600 transition-colors"
                data-testid="search-toggle"
              >
                <Search className="h-5 w-5" strokeWidth={2} />
              </button>

              {/* User Menu */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex items-center space-x-2 px-3 py-2 text-xs font-bold text-black uppercase tracking-wider hover:text-red-600"
                      data-testid="user-menu-trigger"
                    >
                      <User className="h-5 w-5" strokeWidth={2} />
                      <span className="hidden sm:inline">{user.name}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_#000]">
                    <DropdownMenuItem
                      onClick={logout}
                      className="text-black hover:text-red-600 cursor-pointer font-medium"
                      data-testid="logout-btn"
                    >
                      <LogOut className="mr-2 h-4 w-4" strokeWidth={2} />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  onClick={() => setAuthModalOpen(true)}
                  className="bg-black text-white hover:bg-red-600 text-xs font-mono font-bold uppercase tracking-widest px-5 py-2 rounded-none border-0"
                  data-testid="login-btn"
                >
                  Login
                </Button>
              )}

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-black hover:text-red-600"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" strokeWidth={2} /> : <Menu className="h-6 w-6" strokeWidth={2} />}
              </button>
            </div>
          </div>

          {/* Search Bar */}
          {searchOpen && (
            <form onSubmit={handleSearch} className="py-4 border-t-2 border-black">
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-white border-2 border-black focus:border-red-600 focus:ring-0 text-black rounded-none"
                  data-testid="search-input"
                  autoFocus
                />
                <Button
                  type="submit"
                  className="bg-black text-white hover:bg-red-600 rounded-none font-mono uppercase tracking-widest text-xs"
                  data-testid="search-submit"
                >
                  Search
                </Button>
              </div>
            </form>
          )}
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t-2 border-black mobile-menu-enter">
            <div className="px-4 py-4 space-y-1">
              {categories.map((cat) => (
                <div key={cat.slug}>
                  <Link
                    to={`/category/${cat.slug}`}
                    className="block py-3 text-black hover:text-red-600 font-bold text-sm uppercase tracking-wider border-b border-zinc-200"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${cat.slug}`}
                  >
                    {cat.name}
                  </Link>
                  {cat.subcategories.length > 0 && (
                    <div className="pl-4 space-y-0">
                      {cat.subcategories.map((sub) => (
                        <Link
                          key={sub.slug}
                          to={`/category/${cat.slug}/${sub.slug}`}
                          className="block py-2 text-xs text-zinc-600 hover:text-red-600 uppercase tracking-wider font-medium"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </nav>

      <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} />
    </>
  );
}
